import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from '@services/api.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  MAX_SPREAD,
  MIN_SPREAD,
} from '../custom-spread-dialog/custom-spread-dialog.types';
import { DevCycleService } from '@services/dev-cycle.service';

export interface PaymentCustomSpreadData {
  paymentRequestId: string;
  currentSpread?: number;
  defaultSpread?: number;
}

@Component({
  selector: 'ew-payment-custom-spread',
  templateUrl: './payment-custom-spread.component.html',
  styleUrls: ['./payment-custom-spread.component.scss'],
})
export class PaymentCustomSpreadComponent implements OnInit {
  isLoading = false;
  isProcessing = false;
  hasError = false;
  errorMessage = '';

  spreadForm: FormGroup;
  currentSpread: number | null = null;
  defaultSpread: number | null = null;

  minSpread = MIN_SPREAD;
  maxSpread = MAX_SPREAD;

  constructor(
    private fb: FormBuilder,
    private API: ApiService,
    private devCycle: DevCycleService,
    public dialogRef: MatDialogRef<PaymentCustomSpreadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentCustomSpreadData,
  ) {
    this.spreadForm = this.fb.group({
      spread: [
        null,
        [
          Validators.required,
          Validators.min(this.minSpread),
          Validators.max(this.maxSpread),
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
        ],
      ],
      useDefaultSpread: [false],
    });
  }

  ngOnInit() {
    this.isLoading = true;
    this.loadCurrentSpread();
  }

  async loadCurrentSpread() {
    const hasCustomSpreadFF = await this.devCycle.getVariable(
      'ew-2830-custom-spread',
      false,
    );

    if (!hasCustomSpreadFF) {
      return;
    }

    this.API.get(
      `v1/custom-spread/payment-request/${this.data.paymentRequestId}`,
    )
      .pipe(
        catchError((error) => {
          this.hasError = true;
          this.errorMessage =
            error.error?.message || 'Error loading the current spread';
          this.API.alertService.error(this.errorMessage);
          console.error('Error loading the payment spread:', error);
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe((response) => {
        if (response && response.success) {
          this.currentSpread =
            response.records?.paymentSpreadPercentage || null;
          this.defaultSpread = response.records?.customSpreadPercentage || null;

          if (this.currentSpread !== null) {
            this.spreadForm.patchValue({ spread: this.currentSpread });
          } else if (this.defaultSpread !== null) {
            this.spreadForm.patchValue({ spread: this.defaultSpread });
          }
        }
      });
  }

  useDefaultSpreadSettings() {
    if (this.defaultSpread !== null) {
      this.spreadForm.patchValue({
        spread: this.defaultSpread,
        useDefaultSpread: true,
      });
    }
  }

  onSave() {
    if (this.spreadForm.invalid) {
      return;
    }

    const spreadValue = parseFloat(this.spreadForm.get('spread')?.value);
    if (spreadValue === null || spreadValue === undefined) {
      return;
    }

    const useDefaultSpread =
      this.spreadForm.get('useDefaultSpread')?.value || false;

    this.isProcessing = true;
    this.API.post(
      `v1/payment-requests/${this.data.paymentRequestId}/custom-spread`,
      {
        customSpread: spreadValue,
        isDefaultSpread: useDefaultSpread,
      },
    )
      .pipe(
        catchError((error) => {
          this.hasError = true;
          this.errorMessage =
            error.error?.message || 'Error saving the custom spread';
          this.API.alertService.error(this.errorMessage);
          console.error('Error saving the payment spread:', error);
          return of(null);
        }),
        finalize(() => {
          this.isProcessing = false;
        }),
      )
      .subscribe((response) => {
        if (response && response.success) {
          this.API.alertService.success('Custom spread saved successfully');
          this.dialogRef.close(true);
        }
      });
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
